import type { BoardColumn, ClientStatus, Priority, WorkType } from '@rundown/types';
import type { PillTone } from '../components';

/**
 * The words this product uses for its own enums, in one place.
 *
 * ── Why one place ───────────────────────────────────────────────────────────
 * There were three copies — `requests/labels.ts`, `backlog/labels.ts`,
 * `board/labels.ts` — each carrying a comment explaining that it was
 * deliberately re-declared because the other file belonged to somebody else
 * that week. They did not stay identical, and the drift was user-visible:
 * the same `BoardColumn` was labelled "In progress" on the Board and "In
 * development" on the request detail pane, "Review" and "Code review",
 * "Staging" and "On staging". Someone dragged a card into a column and the
 * next screen told them it was somewhere else.
 *
 * @rundown/policy makes this argument about capabilities at length — "the
 * screen that assigns a role and the table that decides what it grants must
 * not be able to disagree". The same applies one layer up, to the words.
 *
 * ── Why here and not in @rundown/types ──────────────────────────────────────
 * Unchanged from the original files' reasoning, which was right: the wire
 * format should not carry English, and the Expo app will want its own phrasing
 * for the same states. The SET of states is shared; the words are this app's.
 */

export const TYPE_LABELS: Readonly<Record<WorkType, string>> = {
  feature: 'Feature',
  bug: 'Bug',
  change: 'Change',
  support: 'Support',
  internal: 'Internal',
};

/**
 * Bug and Change carry weight beyond the word: a Bug in delivered work is
 * retainer, a Change consumes included units. Colouring them differently is not
 * decoration — it is the distinction the whole billing model turns on.
 */
export const TYPE_TONES: Readonly<Record<WorkType, PillTone>> = {
  feature: 'accent',
  bug: 'danger',
  change: 'warn',
  support: 'neutral',
  internal: 'violet',
};

export const STATUS_LABELS: Readonly<Record<ClientStatus, string>> = {
  received: 'Received',
  queued: 'Queued',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  testing: 'Testing',
  released: 'Released',
};

export const STATUS_TONES: Readonly<Record<ClientStatus, PillTone>> = {
  received: 'neutral',
  queued: 'neutral',
  scheduled: 'violet',
  in_progress: 'accent',
  testing: 'warn',
  released: 'success',
};

export const PRIORITY_LABELS: Readonly<Record<Priority, string>> = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

/** Normal and low get no chip at all — a list where everything is badged reads as noise. */
export const PRIORITY_TONES: Readonly<Record<Priority, PillTone | null>> = {
  urgent: 'danger',
  high: 'warn',
  normal: null,
  low: null,
};

export const BOARD_COLUMNS: readonly BoardColumn[] = [
  'triage',
  'ready',
  'in_progress',
  'review',
  'staging',
  'done',
];

/**
 * Noorix's six columns. The client never sees these — they see the
 * `ClientStatus` each maps to (BOARD_COLUMN_TO_CLIENT_STATUS).
 *
 * The wording is the Board's, which is the screen these columns are FOR and
 * the screen people say them out loud beside. The request pane used to say
 * "In development", "Code review" and "On staging" for the same three values;
 * those are the ones that changed.
 */
export const COLUMN_LABELS: Readonly<Record<BoardColumn, string>> = {
  triage: 'Triage',
  ready: 'Ready',
  in_progress: 'In progress',
  review: 'Review',
  staging: 'Staging',
  done: 'Done',
};
